import { StateCreator } from 'zustand'
import type { FFEStore } from '../types'
import type {
  FFEDocument,
  PurchaseOrderSettings,
  SupplierCosts
} from '../../types/ffe'
import { DraftPurchaseOrder } from '../../types/purchaseOrder'

export interface DocumentSlice {
  setSaving: (saving: boolean) => void
  setErrorSaving: (errorSaving: boolean) => void
  setLoadingEditor: (loading: boolean) => void
  setIsBulkImageDrawerOpen: (isOpen: boolean) => void
  setIsPreviewing: (isPreviewing: boolean) => void
  setLineItemDetailsDrawerId: (id: string | undefined) => void
  setImageEditorEntity: (
    entity:
      | {
          id: string
          type: 'lineItem' | 'material'
          materialId?: string
          additionalId?: string
        }
      | undefined
  ) => void
  setDocument: (document: FFEDocument) => void
  updateTitle: (title: string) => void
  updateProjectTitle: (title: string) => void
  updateProjectDescription: (description: string) => void
  updatefooterContent: (description: string) => void
  updateSettings: (settings: FFEDocument['settings']) => void
  updateHeaderContent: (content: string) => void
  updateSupplierCosts: (id: string, costs: SupplierCosts) => void
  updatePurchaseOrderSettings: (settings: PurchaseOrderSettings) => void
  setLoadingDocument: (loading: boolean) => void
  updateCloudId: (id: string) => void
  patchPurchaseOrder: (
    supplierName: string,
    purchaseOrder: Partial<DraftPurchaseOrder>
  ) => void
  setBulkImageUploadDrawerId: (id: string | undefined) => void
}

export const initialDocument: FFEDocument = {
  _id: undefined,
  id: crypto.randomUUID(),
  title: 'New FF&E Schedule',
  projectTitle: '',
  projectDescription: '',
  footerContent: '',
  headerContent: '',
  lineItems: [],
  groups: [],
  materials: [],
  floorPlans: [],
  supplierCosts: [],
  purchaseOrderSettings: {
    deliveryAddress: '',
    deliveryNotes: '',
    contact: {
      name: '',
      phone: '',
      email: ''
    }
  },
  purchaseOrders: [],
  settings: {
    defaultMargin: 30,
    taxInclusive: false,
    enableSpaceLinks: false,
    showClientPrices: true,
    showSupplierDetails: false,
    showTotalValue: true,
    showLeadTimes: true,
    hideMaterialNames: false
  }
}

export const createDocumentSlice: StateCreator<
  FFEStore,
  [],
  [],
  DocumentSlice
> = (set) => ({
  setSaving: (saving) => set({ saving }),
  setErrorSaving: (errorSaving) => set({ errorSaving }),
  setLoadingEditor: (loadingEditor) => set({ loadingEditor }),
  setIsBulkImageDrawerOpen: (isOpen) => set({ isBulkImageDrawerOpen: isOpen }),
  setIsPreviewing: (isPreviewing) => set({ isPreviewing }),
  setLoadingDocument: (loading) => set({ loadingDocument: loading }),
  setLineItemDetailsDrawerId: (id: string | undefined) =>
    set({ lineItemDetailsDrawerId: id }),
  setBulkImageUploadDrawerId: (id: string | undefined) =>
    set({ bulkImageUploadDrawerId: id }),
  setImageEditorEntity: (entity) => set({ imageEditorEntity: entity }),
  setDocument: (document) => set({ document }),

  updateCloudId: (id: string) =>
    set((state) => ({
      document: { ...state.document, _id: id }
    })),

  updateTitle: (title) =>
    set((state) => ({
      document: { ...state.document, title }
    })),

  updateProjectTitle: (projectTitle) =>
    set((state) => ({
      document: { ...state.document, projectTitle }
    })),

  updateProjectDescription: (projectDescription) =>
    set((state) => ({
      document: { ...state.document, projectDescription }
    })),

  updatefooterContent: (footerContent) =>
    set((state) => ({
      document: { ...state.document, footerContent }
    })),

  updateHeaderContent: (headerContent) =>
    set((state) => ({
      document: { ...state.document, headerContent }
    })),

  updateSettings: (settings) =>
    set((state) => ({
      document: { ...state.document, settings }
    })),

  updateSupplierCosts: (id, costs) =>
    set((state) => ({
      document: {
        ...state.document,
        supplierCosts: state.document.supplierCosts.some((c) => c.id === id)
          ? state.document.supplierCosts.map((c) => (c.id === id ? costs : c))
          : [...state.document.supplierCosts, costs]
      }
    })),

  updatePurchaseOrderSettings: (settings) =>
    set((state) => ({
      document: {
        ...state.document,
        purchaseOrderSettings: settings
      }
    })),
  patchPurchaseOrder: (
    supplierName: string,
    purchaseOrder: Partial<DraftPurchaseOrder>
  ) => {
    set((state) => {
      if (!state.document.purchaseOrders) {
        return {
          document: {
            ...state.document,
            purchaseOrders: [
              {
                supplier: supplierName,
                id: crypto.randomUUID(),
                ...purchaseOrder
              }
            ]
          }
        }
      }

      const index = state.document.purchaseOrders.findIndex(
        (po) => po.supplier === supplierName
      )
      if (index === -1) {
        return {
          document: {
            ...state.document,
            purchaseOrders: [
              ...state.document.purchaseOrders,
              {
                supplier: supplierName,
                id: crypto.randomUUID(),
                ...purchaseOrder
              }
            ]
          }
        }
      }

      const purchaseOrders = [...state.document.purchaseOrders]
      purchaseOrders[index] = { ...purchaseOrders[index], ...purchaseOrder }
      return {
        document: {
          ...state.document,
          purchaseOrders
        }
      }
    })
  }
})
